import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Search, Eye } from 'lucide-react';
import { Table, Column } from '../components/ui/Table';
import { api } from '../services/api';
import { Investor } from '../types';

const parseNumber = (value: unknown): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
};

export const KYC: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadInvestors = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getInvestors({ limit: 1000 });
        const raw = response?.data ?? response ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const transformed: Investor[] = list.map((inv: any) => ({
          id: inv.id,
          name: inv.name ?? '',
          email: inv.email ?? '',
          phone: inv.phone ?? '',
          status: inv.status ?? 'active',
          type: inv.type ?? 'fisica',
          totalInvested: parseNumber(inv.totalInvested),
          propertiesCount: parseNumber(inv.propertiesCount),
          curp: inv.curp,
          csf: inv.csf,
          kyc: inv.kyc,
          createdAt: inv.createdAt ?? new Date().toISOString(),
        }));
        setInvestors(transformed);
      } catch (err: any) {
        setError(err.message || 'Error al cargar inversionistas');
        setInvestors([]);
      } finally {
        setLoading(false);
      }
    };
    loadInvestors();
  }, []);

  const filteredInvestors = useMemo(() => {
    if (!searchTerm.trim()) return investors;
    const term = searchTerm.toLowerCase();
    return investors.filter(
      (inv) =>
        inv.name?.toLowerCase().includes(term) ||
        inv.email?.toLowerCase().includes(term) ||
        inv.phone?.toLowerCase().includes(term)
    );
  }, [investors, searchTerm]);

  const columns: Column<Investor>[] = [
    { header: 'Nombre', accessor: 'name', sortable: true },
    { header: 'Email', accessor: 'email', sortable: true },
    { header: 'Teléfono', accessor: 'phone' },
    {
      header: 'Tipo',
      accessor: (row) => {
        if (row.type === 'fisica') return 'Persona Física';
        if (row.type === 'moral') return 'Persona Moral';
        return '-';
      },
    },
    {
      header: 'Total Invertido',
      accessor: (row) => `$${parseNumber(row.totalInvested).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      sortable: true,
    },
    {
      header: 'Propiedades',
      accessor: (row) => row.propertiesCount ?? 0,
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) => {
        const statusConfig: Record<string, { label: string; className: string }> = {
          active: { label: 'Activo', className: 'bg-green-100 text-green-800' },
          inactive: { label: 'Inactivo', className: 'bg-gray-100 text-gray-800' },
        };
        const config = statusConfig[row.status] ?? { label: String(row.status), className: 'bg-gray-100 text-gray-800' };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/kyc/${row.id}`);
          }}
          className="inline-flex items-center justify-center p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 text-sm transition-colors"
          title="Ver detalles KYC"
          aria-label="Ver detalles KYC"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <ShieldCheck className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold text-gray-900">KYC - Know Your Customer</h1>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Mostrando {filteredInvestors.length} de {investors.length} inversionistas
            </div>
            <Table data={filteredInvestors} columns={columns} />
          </>
        )}
      </div>
    </div>
  );
};
